import { useMemo, useCallback } from 'react';
import { useAuth } from '../auth';
import { usePermissionsStore, Permission, DisabledPermission } from '../stores/permissionsStore';

// Re-export types for backwards compatibility
export type { Permission, DisabledPermission };

// Type definitions for resource and action types
export type ResourceType = string;
export type ActionType = string;

/**
 * Hook to access user permissions
 *
 * This hook provides a simple interface to check user permissions.
 * It reads from the Zustand permissions store which is populated during login.
 *
 * @param resource - Optional specific resource to get simplified permissions for
 * @returns Permission object with helper methods and pre-computed permission checks
 */
export function usePermissions(resource?: ResourceType) {
  const { isSuperAdmin } = useAuth();

  // Get permissions from store
  const permissions = usePermissionsStore(state => state.permissions);
  const disabledPermissions = usePermissionsStore(state => state.disabledPermissions);
  const loading = usePermissionsStore(state => state.loading);
  const error = usePermissionsStore(state => state.error);
  const storeHasPermission = usePermissionsStore(state => state.hasPermission);
  const storeHasResourceAccess = usePermissionsStore(state => state.hasResourceAccess);
  const globallyDisabledResources = usePermissionsStore(state => state.globallyDisabledResources);
  const resourcePermissions = usePermissionsStore(state => state.getResourcePermissions());

  // Wrap permission checks with SuperAdmin bypass - memoized to prevent infinite loops
  const hasPermission = useCallback((resource: string, action: string): boolean => {
    if (isSuperAdmin) return true;
    return storeHasPermission(resource, action);
  }, [isSuperAdmin, storeHasPermission]);

  const hasResourceAccess = useCallback((resource: string): boolean => {
    if (isSuperAdmin) return true;
    return storeHasResourceAccess(resource);
  }, [isSuperAdmin, storeHasResourceAccess]);

  // Get accessible resources
  const accessibleResources = useMemo(() => {
    return Object.keys(resourcePermissions).filter(resource =>
      resourcePermissions[resource].size > 0
    );
  }, [resourcePermissions]);

  // If a specific resource was requested, return simplified permission object
  if (resource) {
    return {
      canRead: hasPermission(resource, 'view'),
      canCreate: hasPermission(resource, 'create'),
      canUpdate: hasPermission(resource, 'update'),
      canDelete: hasPermission(resource, 'delete'),
      canManage: hasPermission(resource, 'manage'),
      canAssign: hasPermission(resource, 'assign'),
      canArchive: hasPermission(resource, 'archive'),
      canImport: hasPermission(resource, 'import'),
      canConvert: hasPermission(resource, 'convert'),
      canFilterByUser: hasPermission(resource, 'filter_by_user'),
      loading,
      hasPermission,
    };
  }

  // Otherwise return full permission object with all pre-computed permissions
  return {
    permissions,
    loading,
    error,
    hasPermission,
    hasResourceAccess,
    accessibleResources,
    resourcePermissions,

    // Feature-level permission checks
    canGenerateDealPDF: hasPermission('deals', 'generate'),

    // NEW: General permission query methods
    can: (resource: ResourceType, action: ActionType) => hasPermission(resource, action),
    canAccess: (resource: ResourceType) => hasResourceAccess(resource),

    // Client permissions
    canCreateClient: hasPermission('clients', 'create'),
    canEditClient: hasPermission('clients', 'update'),
    canViewClient: hasPermission('clients', 'view'),
    canDeleteClient: hasPermission('clients', 'delete'),
    canAccessClients: hasResourceAccess('clients'),

    // Deal permissions
    canCreateDeal: hasPermission('deals', 'create'),
    canEditDeal: hasPermission('deals', 'update'),
    canViewDeal: hasPermission('deals', 'view'),
    canDeleteDeal: hasPermission('deals', 'delete'),
    canAccessDeals: hasResourceAccess('deals'),

    // Document permissions
    canCreateDocument: hasPermission('documents', 'create'),
    canViewDocument: hasPermission('documents', 'view'),
    canDeleteDocument: hasPermission('documents', 'delete'),
    canAccessDocuments: hasResourceAccess('documents'),

    // Projects permissions
    canCreateProject: hasPermission('projects', 'create'),
    canEditProject: hasPermission('projects', 'update'),
    canViewProject: hasPermission('projects', 'view'),
    canDeleteProject: hasPermission('projects', 'delete'),
    canAccessProjects: hasResourceAccess('projects'),

    // Calendar permissions
    canCreateEvent: hasPermission('calendar', 'create'),
    canEditEvent: hasPermission('calendar', 'update'),
    canViewCalendar: hasPermission('calendar', 'view'),
    canDeleteEvent: hasPermission('calendar', 'delete'),
    canAccessCalendar: hasResourceAccess('calendar'),

    // Reports permissions
    canCreateReport: hasPermission('reports', 'create'),
    canViewReport: hasPermission('reports', 'view'),
    canDeleteReport: hasPermission('reports', 'delete'),
    canAccessReports: hasResourceAccess('reports'),

    // User and role permissions
    canCreateUser: hasPermission('users', 'create'),
    canEditUser: hasPermission('users', 'update'),
    canViewUser: hasPermission('users', 'view'),
    canDeleteUser: hasPermission('users', 'delete'),
    canAccessUsers: hasResourceAccess('users'),

    canCreateRole: hasPermission('roles', 'create'),
    canEditRole: hasPermission('roles', 'update'),
    canViewRole: hasPermission('roles', 'view'),
    canDeleteRole: hasPermission('roles', 'delete'),
    canAccessRoles: hasResourceAccess('roles'),

    // Inventory permissions
    canCreateInventory: hasPermission('inventory', 'create'),
    canEditInventory: hasPermission('inventory', 'update'),
    canViewInventory: hasPermission('inventory', 'view'),
    canDeleteInventory: hasPermission('inventory', 'delete'),
    canAccessInventory: hasResourceAccess('inventory'),

    // Service permissions
    canCreateService: hasPermission('services', 'create'),
    canEditService: hasPermission('services', 'update'),
    canViewService: hasPermission('services', 'view'),
    canDeleteService: hasPermission('services', 'delete'),
    canManageService: hasPermission('services', 'manage'),
    canAccessServices: hasResourceAccess('services'),

    // Ticket permissions
    canCreateTicket: hasPermission('tickets', 'create'),
    canEditTicket: hasPermission('tickets', 'update'),
    canViewTicket: hasPermission('tickets', 'view'),
    canDeleteTicket: hasPermission('tickets', 'delete'),
    canAssignTicket: hasPermission('tickets', 'assign'),
    canViewTicketReports: hasPermission('tickets', 'report'),
    canExportTickets: hasPermission('tickets', 'export'),
    canAccessTickets: hasResourceAccess('tickets'),

    // Ticket Types permissions (Tipi di Intervento)
    canCreateTicketType: hasPermission('ticket_types', 'create'),
    canEditTicketType: hasPermission('ticket_types', 'update'),
    canViewTicketType: hasPermission('ticket_types', 'view'),
    canDeleteTicketType: hasPermission('ticket_types', 'delete'),
    canManageTicketTypes: hasPermission('ticket_types', 'manage'),
    canAccessTicketTypes: hasResourceAccess('ticket_types'),

    // Ticket Time Entries permissions (Tracciamento Ore)
    canCreateTimeEntry: hasPermission('ticket_time_entries', 'create'),
    canEditTimeEntry: hasPermission('ticket_time_entries', 'update'),
    canViewTimeEntry: hasPermission('ticket_time_entries', 'view'),
    canDeleteTimeEntry: hasPermission('ticket_time_entries', 'delete'),
    canManageTimeEntries: hasPermission('ticket_time_entries', 'manage'),
    canAccessTimeEntries: hasResourceAccess('ticket_time_entries'),

    // Ticket SLA Config permissions
    canCreateSLAConfig: hasPermission('ticket_sla_configs', 'create'),
    canEditSLAConfig: hasPermission('ticket_sla_configs', 'update'),
    canViewSLAConfig: hasPermission('ticket_sla_configs', 'view'),
    canDeleteSLAConfig: hasPermission('ticket_sla_configs', 'delete'),
    canManageSLAConfigs: hasPermission('ticket_sla_configs', 'manage'),
    canAccessSLAConfigs: hasResourceAccess('ticket_sla_configs'),

    // Deal Hours Package permissions (Monte Ore)
    canViewHoursPackage: hasPermission('deals', 'hours_view'),
    canUpdateHoursPackage: hasPermission('deals', 'hours_update'),

    // Category permissions
    canCreateCategory: hasPermission('categories', 'create'),
    canEditCategory: hasPermission('categories', 'update'),
    canViewCategory: hasPermission('categories', 'view'),
    canDeleteCategory: hasPermission('categories', 'delete'),
    canAccessCategories: hasResourceAccess('categories'),

    // Template permissions
    canCreateTemplate: hasPermission('templates', 'create'),
    canEditTemplate: hasPermission('templates', 'update'),
    canViewTemplate: hasPermission('templates', 'view'),
    canDeleteTemplate: hasPermission('templates', 'delete'),
    canAccessTemplates: hasResourceAccess('templates'),

    // API Configuration permissions
    canCreateApiConfig: hasPermission('api_config', 'create'),
    canEditApiConfig: hasPermission('api_config', 'update'),
    canViewApiConfig: hasPermission('api_config', 'view'),
    canDeleteApiConfig: hasPermission('api_config', 'delete'),
    canAccessApiConfig: hasResourceAccess('api_config'),

    // Payment method permissions
    canCreatePaymentMethod: hasPermission('payment_methods', 'create'),
    canEditPaymentMethod: hasPermission('payment_methods', 'update'),
    canViewPaymentMethod: hasPermission('payment_methods', 'view'),
    canDeletePaymentMethod: hasPermission('payment_methods', 'delete'),
    canAccessPaymentMethods: hasResourceAccess('payment_methods'),

    // Commission permissions
    canViewCommission: hasPermission('commissions', 'view'),
    canManageCommission: hasPermission('commissions', 'update'),
    canAccessCommissions: hasResourceAccess('commissions'),

    // Settings permissions
    canViewSettings: hasPermission('settings', 'view'),
    canUpdateSettings: hasPermission('settings', 'update'),
    canImportExport: hasPermission('settings_import_export', 'view'),
    canAccessSettings: hasResourceAccess('settings'),

    // Dashboard permissions
    canViewDashboard: hasPermission('dashboard', 'view'),
    canAccessDashboard: hasResourceAccess('dashboard'),

    // Analytics permissions
    canViewAnalytics: hasPermission('analytics', 'view'),
    canAccessAnalytics: hasResourceAccess('analytics'),

    // Signature permissions
    canCreateSignature: hasPermission('signatures', 'create'),
    canViewSignature: hasPermission('signatures', 'view'),
    canAccessSignatures: hasResourceAccess('signatures'),

    // WhatsApp permissions
    canViewWhatsApp: hasPermission('whatsapp', 'view'),
    canSendWhatsAppMessages: hasPermission('whatsapp', 'create'),
    canViewOnlyAssignedWhatsAppChats: hasPermission('whatsapp', 'assign'),
    canAccessWhatsApp: hasResourceAccess('whatsapp'),

    // Referrers permissions
    canCreateReferrer: hasPermission('referrers', 'create'),
    canEditReferrer: hasPermission('referrers', 'update'),
    canViewReferrer: hasPermission('referrers', 'view'),
    canDeleteReferrer: hasPermission('referrers', 'delete'),
    canAccessReferrers: hasResourceAccess('referrers'),

    // FattureInCloud permissions (CRITICAL)
    canViewFattureInCloud: hasPermission('fattureincloud', 'view'),
    canSyncFattureInCloud: hasPermission('fattureincloud', 'sync'),
    canConfigureFattureInCloud: hasPermission('fattureincloud', 'configure'),
    canAccessFattureInCloud: hasResourceAccess('fattureincloud'),

    // Bank Movements (Prima Nota) permissions
    canViewBankMovements: hasPermission('bank_movements', 'view'),

    // Financial Dashboard permissions
    canViewFinancialDashboard: hasPermission('financial_dashboard', 'view'),
    canManageExpenseCategories: hasPermission('expense_categories', 'manage'),
    canUseFinancialChat: hasPermission('financial_dashboard', 'generate'),

    // My Tasks permissions
    canCreateTask: hasPermission('tasks', 'create'),
    canEditTask: hasPermission('tasks', 'update'),
    canDeleteTask: hasPermission('tasks', 'delete'),
    canCompleteTask: hasPermission('tasks', 'complete'),
    canAssignTask: hasPermission('tasks', 'assign'),
    canAccessTasks: hasResourceAccess('tasks'),

    // User Commissions permissions (CRITICAL)
    canViewUserCommissions: hasPermission('user_commissions', 'view'),
    canEditUserCommissions: hasPermission('user_commissions', 'update'),
    canManageUserCommissions: hasPermission('user_commissions', 'manage'),
    canAccessUserCommissions: hasResourceAccess('user_commissions'),

    // Partner permissions
    canCreatePartner: hasPermission('external_partners', 'create'),
    canEditPartner: hasPermission('external_partners', 'update'),
    canDeletePartner: hasPermission('external_partners', 'delete'),
    canAccessPartners: hasResourceAccess('external_partners'),

    // Partner Task permissions
    canCreatePartnerTask: hasPermission('partner_tasks', 'create'),
    canEditPartnerTask: hasPermission('partner_tasks', 'update'),
    canDeletePartnerTask: hasPermission('partner_tasks', 'delete'),
    canAssignPartnerTask: hasPermission('partner_tasks', 'assign'),
    canAccessPartnerTasks: hasResourceAccess('partner_tasks'),

    // Service Cost permissions
    canCreateServiceCost: hasPermission('service_costs', 'create'),
    canEditServiceCost: hasPermission('service_costs', 'update'),
    canDeleteServiceCost: hasPermission('service_costs', 'delete'),
    canViewServiceMargins: hasPermission('service_margins', 'view'),
    canAccessServiceCosts: hasResourceAccess('service_costs'),

    // Service Task Template permissions
    canCreateServiceTaskTemplate: hasPermission('service_task_templates', 'create'),
    canEditServiceTaskTemplate: hasPermission('service_task_templates', 'update'),
    canDeleteServiceTaskTemplate: hasPermission('service_task_templates', 'delete'),
    canAccessServiceTaskTemplates: hasResourceAccess('service_task_templates'),

    // Commission permissions (advanced)
    canManageCommissions: hasPermission('commissions', 'manage'),
    canRefreshCommissions: hasPermission('commissions', 'refresh'),

    // Analytics permissions
    canRefreshAnalytics: hasPermission('analytics', 'refresh'),

    // Report permissions (additional)
    canEditReport: hasPermission('reports', 'update'),
    canExportReport: hasPermission('reports', 'export'),
    canExecuteReport: hasPermission('reports', 'execute'),
    canScheduleReport: hasPermission('reports', 'schedule'),
    canShareReport: hasPermission('reports', 'share'),

    // Dashboard Analytics permissions
    canViewDashboards: hasPermission('dashboards', 'view'),
    canCreateDashboards: hasPermission('dashboards', 'create'),
    canEditDashboards: hasPermission('dashboards', 'update'),
    canDeleteDashboards: hasPermission('dashboards', 'delete'),
    canShareDashboards: hasPermission('dashboards', 'share'),
    canAccessDashboards: hasResourceAccess('dashboards'),

    // KPIs permissions
    canViewKPIs: hasPermission('kpis', 'view'),
    canCreateKPIs: hasPermission('kpis', 'create'),
    canEditKPIs: hasPermission('kpis', 'update'),
    canDeleteKPIs: hasPermission('kpis', 'delete'),
    canAccessKPIs: hasResourceAccess('kpis'),

    // Insights permissions
    canViewInsights: hasPermission('insights', 'view'),
    canDismissInsights: hasPermission('insights', 'dismiss'),
    canAccessInsights: hasResourceAccess('insights'),

    // Expense permissions
    canCreateExpense: hasPermission('client_expenses', 'create'),
    canEditExpense: hasPermission('client_expenses', 'update'),
    canDeleteExpense: hasPermission('client_expenses', 'delete'),
    canViewExpense: hasPermission('client_expenses', 'view'),
    canAccessExpenses: hasResourceAccess('client_expenses'),

    // Product/Inventory permissions
    canCreateProduct: hasPermission('inventory', 'create'),
    canEditProduct: hasPermission('inventory', 'update'),
    canDeleteProduct: hasPermission('inventory', 'delete'),
    canViewProduct: hasPermission('inventory', 'view'),

    // Rental permissions
    canCreateRental: hasPermission('rentals', 'create'),
    canEditRental: hasPermission('rentals', 'update'),
    canDeleteRental: hasPermission('rentals', 'delete'),
    canViewRental: hasPermission('rentals', 'view'),

    // Helper functions
    isRestrictedToAssignedWhatsAppChats: () => hasPermission('whatsapp', 'assign'),

    // Leads permissions
    canCreateLead: hasPermission('leads', 'create'),
    canEditLead: hasPermission('leads', 'update'),
    canViewLead: hasPermission('leads', 'view'),
    canDeleteLead: hasPermission('leads', 'delete'),
    canConvertLead: hasPermission('leads', 'convert'),
    canAccessLeads: hasResourceAccess('leads'),

    // User filtering permissions
    canFilterLeadsByUser: hasPermission('leads', 'filter_by_user'),
    canFilterClientsByUser: hasPermission('clients', 'filter_by_user'),
    canFilterDashboardByUser: hasPermission('dashboard', 'filter_by_user'),
    canFilterDealsByUser: hasPermission('deals', 'filter_by_user'),

    // Newsletter permissions
    canCreateNewsletter: hasPermission('newsletter', 'create'),
    canEditNewsletter: hasPermission('newsletter', 'update'),
    canViewNewsletter: hasPermission('newsletter', 'view'),
    canDeleteNewsletter: hasPermission('newsletter', 'delete'),
    canSendNewsletter: hasPermission('newsletter', 'send'),
    canAccessNewsletter: hasResourceAccess('newsletter'),

    // SendGrid domain permissions
    canViewSendGridDomains: hasPermission('settings_sendgrid', 'view'),
    canCreateSendGridDomain: hasPermission('settings_sendgrid', 'create'),
    canEditSendGridDomain: hasPermission('settings_sendgrid', 'update'),
    canDeleteSendGridDomain: hasPermission('settings_sendgrid', 'delete'),
    canVerifySendGridDomain: hasPermission('settings_sendgrid', 'verify'),
    canAccessSendGridSettings: hasResourceAccess('settings_sendgrid'),

    // Booking permissions
    canViewBooking: hasPermission('booking', 'view'),
    canCreateBooking: hasPermission('booking', 'create'),
    canEditBooking: hasPermission('booking', 'update'),
    canDeleteBooking: hasPermission('booking', 'delete'),
    canManageBooking: hasPermission('booking', 'manage'),
    canFilterBookingsByUser: hasPermission('booking', 'filter_by_user'),
    canAccessBooking: hasResourceAccess('booking'),

    // Survey permissions
    canViewSurvey: hasPermission('surveys', 'view'),
    canCreateSurvey: hasPermission('surveys', 'create'),
    canEditSurvey: hasPermission('surveys', 'update'),
    canDeleteSurvey: hasPermission('surveys', 'delete'),
    canAccessSurveys: hasResourceAccess('surveys'),

    // Marketing permissions
    canViewMarketing: hasPermission('marketing', 'view'),
    canCreateMarketing: hasPermission('marketing', 'create'),
    canEditMarketing: hasPermission('marketing', 'update'),
    canDeleteMarketing: hasPermission('marketing', 'delete'),
    canAccessMarketing: hasResourceAccess('marketing'),

    // AI Features permissions (per-feature)
    canUseAITextGeneration: hasPermission('ai_text_generation', 'view'),
    canUseAITaskGeneration: hasPermission('ai_task_generation', 'view'),
    canUseAISuggestions: hasPermission('ai_suggestions', 'view'),
    canUseAIUpsellScanner: hasPermission('ai_upsell_scanner', 'view'),
    canUseAIInsights: hasPermission('ai_insights', 'view'),
    canUseAIWhatsAppAgent: hasPermission('ai_whatsapp_agent', 'view'),
    canManageAIWhatsAppAgent: hasPermission('ai_whatsapp_agent', 'manage'),
    canUseAIMeetingBot: hasPermission('ai_meeting_bot', 'view'),
    canManageAIMeetingBot: hasPermission('ai_meeting_bot', 'manage'),
    canUseAIStatusUpdates: hasPermission('ai_status_updates', 'view'),
    canManageAIStatusUpdates: hasPermission('ai_status_updates', 'manage'),

    // Globally disabled features
    globallyDisabledResources,
    isResourceGloballyDisabled: (resource: string) => globallyDisabledResources.includes(resource),

    // Expose raw permissions array for subscription filtering
    availablePermissions: permissions
  };
}

// Export clearPermissionsCache for backwards compatibility (deprecated)
// Components should use usePermissionsStore().refreshPermissions() instead
export function clearPermissionsCache(options?: { force?: boolean }) {
  console.warn('[usePermissions] clearPermissionsCache is deprecated, use usePermissionsStore().refreshPermissions() instead');
  // No-op for backwards compatibility
}
